import { useQuery } from '@tanstack/react-query';
import type { Team, Translated } from '@uefadigital/panenka';
import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ChangeEvent } from 'react';
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import { getRoundParticipantsQuery } from '@queries/competition';
import { getAllTeamsQuery } from '@queries/teams';
import { getTeamUrl } from '@services/LinkRuleService';
import { hydrateWithTranslations } from '@services/TranslatorService';
import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import TeamsDisclaimer from '@shared/TeamsDisclaimer';
import { windowized } from '@shared/Windowizer';
import type { Enum } from '@shared-types/enum.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { removeDiacritics } from '@utilities/text-manipulation';

import * as classes from './styles.module.scss';

const WindowizedTeamIdentifier = memo(windowized(TeamIdentifier));

export const SearchType = {
  DROPDOWN: 'dropdown',
  FULLSCREEN: 'fullscreen',
} as const;
export type SearchType = Enum<typeof SearchType>;

const activationThreshold = 2;
const ClubSearch = ({
  options,
  onClubsLoad,
  onItemClick,
}: {
  options: any;
  onClubsLoad?: (clubs: Translated<Team>[]) => unknown;
  onItemClick?: (club: Translated<Team>) => unknown;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedClub, setSelectedClub] = useState<Translated<Team>>(null);

  const { data: roundParticipants } = useQuery({
    ...getRoundParticipantsQuery({
      competitionId: options.cup,
      seasonYear: 'current',
      phase: options.phase,
    }),
    select: (data) => data.find((rp) => rp.round.active === true || rp.round.status !== 'FINISHED')?.teams,
    placeholderData: [],
    enabled: !!options.showOnlyActiveTeams,
  });

  const { data: allTeams } = useQuery({
    ...getAllTeamsQuery({
      competitionId: options.cup,
      seasonYear: options.seasonYear ? options.seasonYear : options.phase ? 'current' : null,
      phase: options.phase,
      limit: 600,
    }),
    placeholderData: [],
    enabled: !options.showOnlyActiveTeams,
  });

  const clubs = useMemo(
    () =>
      (options.showOnlyActiveTeams ? roundParticipants : allTeams)
        .map((c) => hydrateWithTranslations(c))
        .toSorted((prev, next) => (prev.displayName < next.displayName ? -1 : 1)),
    [allTeams, options.showOnlyActiveTeams, roundParticipants],
  );

  useEffect(() => {
    if (onClubsLoad && clubs?.length) {
      onClubsLoad(clubs);
    }
  }, [clubs, onClubsLoad]);

  const searchTextDeferred = useDeferredValue(searchText);
  const filteredClubs = useMemo(
    () =>
      clubs.filter(
        (club) =>
          (options.type === SearchType.FULLSCREEN && !searchTextDeferred) ||
          (searchTextDeferred && searchTextDeferred.length < activationThreshold) ||
          removeDiacritics(club.displayName.toLowerCase()).includes(removeDiacritics(searchTextDeferred.toLowerCase())),
      ),
    [clubs, options.type, searchTextDeferred],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setSelectedClub(null);

    options.type === SearchType.DROPDOWN && onItemClick?.({ id: null });
  };

  const handleBlur = () => {
    if (options.type === SearchType.DROPDOWN) {
      return;
    }
  };

  const goToClubPage = (club: Team) => {
    GoogleTagManagerHelper.ga4Event('history_click', {
      interaction: 'Select club in autocomplete',
    });
    const url = getTeamUrl(club.id, options.cup, !!options.isHistory, options.phase);
    window.location.href = url;
  };

  const handleClubClick = (club: Translated<Team>) => {
    if (options.type === SearchType.DROPDOWN) {
      setSelectedClub(club);
    }

    onItemClick(club);
  };

  const handleItemClick = (club: Translated<Team>) => () => {
    setSearchText('');
    return onItemClick ? handleClubClick(club) : goToClubPage(club);
  };

  const intersectionOptions = useMemo(
    () => ({
      root: scrollRef.current,
      rootMargin: '200px 0px',
    }),
    [],
  );

  return (
    <div
      className={clsx({
        [classes.wrapper]: true,
        [classes.dropdown]: options.type !== SearchType.FULLSCREEN,
        [classes.fullscreen]: options.type === SearchType.FULLSCREEN,
      })}
    >
      <div
        className={clsx({
          [classes.search]: true,
        })}
      >
        <div className={classes.searchWrapper}>
          <PkIcon className={classes.iconSearch} color='interaction' name='actions-search' />
          {selectedClub && options.type === SearchType.DROPDOWN && (
            <img
              src={selectedClub.mediumLogoUrl}
              className='club-logo'
              title={selectedClub.displayName}
              alt={selectedClub.displayName}
            />
          )}
          <input
            type='text'
            disabled={options.disabled}
            className={classes.input}
            placeholder={options.placeholder}
            onChange={handleSearch}
            onBlur={handleBlur}
            value={options.disabled ? options.placeholder : selectedClub ? selectedClub.displayName : searchText}
          />
        </div>
      </div>

      {!selectedClub && (options.type === SearchType.FULLSCREEN || !!searchText) && (
        <div className={clsx(classes.list, window.showTeamsDisclaimer && classes.disclaimer)} ref={scrollRef}>
          {filteredClubs.map((club) => (
            <WindowizedTeamIdentifier
              windowizeClassName={classes.placeholder}
              intersectionOptions={intersectionOptions}
              key={club.id}
              team={club}
              className={classes.identifier}
              enableSelection
              onIdentifierSelected={handleItemClick(club)}
              selected={selectedClub?.id === club.id}
              type={options.type === SearchType.FULLSCREEN ? 'vertical' : 'horizontal'}
              showTeamCountry={options.type === SearchType.FULLSCREEN}
              badgeSize={options.badgeSize || 48}
            />
          ))}
        </div>
      )}
      {window.showTeamsDisclaimer && (
        <div className={classes.disclaimer}>
          <TeamsDisclaimer />
        </div>
      )}
    </div>
  );
};

export default memo(ClubSearch);
